// AGC: SupplyMovementHistory - Bank statement style movement log
// AGC: Shows all entries and exits for a supply item

import React, { useEffect, useState, useRef } from 'react'
import { 
  ArrowUp, 
  ArrowDown, 
  Calendar,
  MapPin,
  ClipboardList,
  User,
  Loader2,
  Package,
  Smartphone,
  Mic,
  Monitor,
} from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'
import { useInventory } from '../../hooks/useInventory'
import type { InventoryMovement } from '../../types/inventory'
import { format, formatDistanceToNow } from 'date-fns'
import { es, pt, enUS } from 'date-fns/locale'

// =============================================================================
// AGC: TYPES
// =============================================================================

export interface SupplyMovementHistoryProps {
  inventoryItemId?: string  // AGC: null if item doesn't exist in inventory yet
  inputName: string
  unit: string
}

// =============================================================================
// AGC: MOVEMENT ITEM COMPONENT
// =============================================================================

interface MovementItemProps {
  movement: InventoryMovement
  unit: string
  locale: Locale
  fallbackUserName?: string
}

// AGC: Source channel icon mapping
const SourceIcon: React.FC<{ source: string; className?: string }> = ({ source, className = 'w-3 h-3' }) => {
  switch (source) {
    case 'whatsapp': return <Smartphone className={className} />
    case 'voice': return <Mic className={className} />
    case 'app': return <Monitor className={className} />
    default: return <User className={className} />
  }
}

// AGC: Source display label
const sourceLabel = (source: string): string => {
  switch (source) {
    case 'whatsapp': return 'WhatsApp'
    case 'voice': return 'Voz (App)'
    case 'app': return 'App'
    case 'manual': return 'Manual'
    case 'task_consumption': return 'Tarefa'
    case 'invoice_scan': return 'Nota Fiscal'
    case 'system': return 'Sistema'
    default: return source || 'Manual'
  }
}

const MovementItem: React.FC<MovementItemProps> = ({ movement, unit, locale, fallbackUserName }) => {
  const { t } = useTranslation()
  const isEntry = movement.type === 'entry'
  const [showUserInfo, setShowUserInfo] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)
  // AGC: Use stored createdByName, or fall back to current user's name (since this is their data)
  const displayName = movement.createdByName || fallbackUserName || t('common.unknownUser') || 'Usuário'
  
  // AGC: Format date
  const movementDate = new Date(movement.createdAt)
  const dateFormatted = format(movementDate, 'dd/MM/yyyy', { locale })
  const timeFormatted = format(movementDate, 'HH:mm', { locale })
  
  // AGC: Get reason/description
  const getDescription = () => {
    if (movement.reason) return movement.reason
    if (movement.notes) return movement.notes
    if (isEntry) return t('supplies.movements.defaultEntry') || 'Entrada de estoque'
    return t('supplies.movements.defaultExit') || 'Saída de estoque'
  }

  // AGC: Close popover on outside click
  useEffect(() => {
    if (!showUserInfo) return
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setShowUserInfo(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showUserInfo])
  
  return (
    <div className="flex items-start gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
      {/* AGC: Entry/Exit icon */}
      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
        isEntry 
          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
          : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
      }`}>
        {isEntry ? (
          <ArrowDown className="w-5 h-5" />
        ) : (
          <ArrowUp className="w-5 h-5" />
        )}
      </div>
      
      {/* AGC: Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {/* AGC: Main description */}
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {getDescription()}
            </p>
            
            {/* AGC: Additional info */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {dateFormatted} {timeFormatted}
              </span>
              
              {movement.relatedLotId && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {t('supplies.movements.lot') || 'Lote'}: {movement.relatedLotId}
                </span>
              )}
              
              {movement.relatedTaskId && (
                <span className="flex items-center gap-1">
                  <ClipboardList className="w-3 h-3" />
                  {t('supplies.movements.task') || 'Tarefa'}
                </span>
              )}

              {/* AGC: Source channel badge */}
              {movement.source && (
                <span className="flex items-center gap-1 capitalize">
                  <SourceIcon source={movement.source} />
                  {sourceLabel(movement.source)}
                </span>
              )}
            </div>
          </div>
          
          {/* AGC: Right side — quantity + user icon */}
          <div className="flex items-start gap-2 flex-shrink-0">
            {/* AGC: Quantity change */}
            <div className="text-right">
              <p className={`text-sm font-bold ${
                isEntry 
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {isEntry ? '+' : '-'}{Math.abs(movement.quantity).toFixed(1)} {unit}
              </p>
              
              <p className="text-xs text-gray-400 mt-0.5">
                {t('supplies.movements.balance') || 'Saldo'}: {movement.newQuantity.toFixed(1)} {unit}
              </p>
            </div>

            {/* AGC: User avatar icon — click to see who made this entry */}
            <div className="relative" ref={popoverRef}>
              <button
                onClick={() => setShowUserInfo(!showUserInfo)}
                className="w-7 h-7 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center hover:bg-blue-100 dark:hover:bg-blue-800/40 transition-colors"
                title={displayName}
              >
                <User className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
              </button>

              {/* AGC: Popover — who made this movement */}
              {showUserInfo && (
                <div className="absolute right-0 top-9 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 min-w-[180px] animate-in fade-in slide-in-from-top-1 duration-150">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                      <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {displayName}
                      </p>
                      <p className="text-[10px] text-gray-400 truncate">
                        {dateFormatted} {timeFormatted}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 pt-1.5 border-t border-gray-100 dark:border-gray-700">
                    <SourceIcon source={movement.source} className="w-3.5 h-3.5" />
                    <span>{t('supplies.movements.via') || 'Via'}: {sourceLabel(movement.source)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* AGC: Cost info if available */}
        {movement.costPerUnit && movement.costPerUnit > 0 && (
          <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg text-xs">
            <span className="text-gray-500 dark:text-gray-400">
              {t('supplies.movements.cost') || 'Custo'}: 
            </span>
            <span className="font-medium text-gray-700 dark:text-gray-300 ml-1">
              R$ {movement.costPerUnit.toFixed(2)}/{unit}
            </span>
            {movement.totalCost && (
              <span className="text-gray-400 ml-2">
                (Total: R$ {movement.totalCost.toFixed(2)})
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// =============================================================================
// AGC: MAIN COMPONENT
// =============================================================================

export const SupplyMovementHistory: React.FC<SupplyMovementHistoryProps> = ({
  inventoryItemId,
  inputName,
  unit,
}) => {
  const { t, language } = useTranslation()
  const { user, profile } = useAuth()
  const { getMovementsForItem } = useInventory()
  // AGC: Fallback name for movements created before createdByName was stored.
  // AGC: Since all inventory is per-user, the current user is the owner of these movements.
  const currentUserName = user?.displayName || profile?.displayName || null
  
  const [movements, setMovements] = useState<InventoryMovement[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // AGC: Get date-fns locale
  const getLocale = (): Locale => {
    switch (language) {
      case 'es': return es
      case 'pt': return pt
      default: return enUS
    }
  }
  
  // AGC: Fetch movements
  useEffect(() => {
    const fetchMovements = async () => {
      if (!inventoryItemId) {
        setMovements([])
        setLoading(false)
        return
      }
      
      setLoading(true)
      setError(null)
      
      try {
        if (getMovementsForItem) {
          const data = await getMovementsForItem(inventoryItemId)
          // AGC: Defensive — ensure data is an array before spreading
          const arr = Array.isArray(data) ? data : []
          const sorted = [...arr].sort((a, b) => 
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
          setMovements(sorted)
        } else {
          setMovements([])
        }
      } catch (err) {
        console.error('AGC: Error fetching movements:', err)
        setError(t('supplies.movements.fetchError') || 'Error al cargar movimientos')
      } finally {
        setLoading(false)
      }
    }
    
    fetchMovements()
  }, [inventoryItemId, getMovementsForItem, t])
  
  // AGC: Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    )
  }
  
  // AGC: Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-3">
          <Package className="w-6 h-6 text-red-500" />
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm">{error}</p>
      </div>
    )
  }
  
  // AGC: No inventory item yet
  if (!inventoryItemId) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
          <Package className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-gray-600 dark:text-gray-300 font-medium mb-1">
          {t('supplies.movements.noInventory') || 'Sin registro de inventario'}
        </h3>
        <p className="text-gray-400 text-sm max-w-[280px]">
          {t('supplies.movements.noInventoryDesc') || 
            'Este insumo aún no ha sido registrado en tu inventario. Agregá stock para comenzar a rastrear movimientos.'}
        </p>
      </div>
    )
  }
  
  // AGC: Empty state
  if (movements.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
          <ArrowDown className="w-6 h-6 text-emerald-500" />
          <ArrowUp className="w-6 h-6 text-red-500 -ml-2" />
        </div>
        <h3 className="text-gray-600 dark:text-gray-300 font-medium mb-1">
          {t('supplies.movements.empty') || 'Sin movimientos'}
        </h3>
        <p className="text-gray-400 text-sm max-w-[280px]">
          {t('supplies.movements.emptyDesc') || 
            'Todavía no hay movimientos registrados para este insumo. Las entradas y salidas aparecerán aquí.'}
        </p>
      </div>
    )
  }
  
  // AGC: Group movements by date
  const groupedMovements = movements.reduce((acc, movement) => {
    const date = format(new Date(movement.createdAt), 'yyyy-MM-dd')
    if (!acc[date]) {
      acc[date] = []
    }
    acc[date].push(movement)
    return acc
  }, {} as Record<string, InventoryMovement[]>)
  
  const locale = getLocale()
  
  return (
    <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
      {Object.entries(groupedMovements).map(([dateKey, dateMovements]) => {
        const date = new Date(dateKey)
        const dateLabel = format(date, "EEEE, d 'de' MMMM", { locale })
        const isToday = format(new Date(), 'yyyy-MM-dd') === dateKey
        const isYesterday = format(new Date(Date.now() - 86400000), 'yyyy-MM-dd') === dateKey
        
        let displayDate = dateLabel
        if (isToday) {
          displayDate = t('common.today') || 'Hoy'
        } else if (isYesterday) {
          displayDate = t('common.yesterday') || 'Ayer'
        }
        
        return (
          <div key={dateKey}>
            {/* AGC: Date header */}
            <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900/50 sticky top-0">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                {displayDate}
              </p>
            </div>
            
            {/* AGC: Movements for this date */}
            <div className="divide-y divide-gray-50 dark:divide-gray-800">
              {dateMovements.map((movement) => (
                <MovementItem 
                  key={movement.id} 
                  movement={movement} 
                  unit={unit}
                  locale={locale}
                  fallbackUserName={currentUserName}
                />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default SupplyMovementHistory
